import { useState, useEffect } from 'react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Activity, 
  Handshake, 
  Award, 
  Building, 
  ShoppingBag, 
  Calendar, 
  GalleryHorizontal, 
  Mail, 
  Phone, 
  Facebook, 
  Linkedin, 
  Contact 
} from "lucide-react";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import LoadingScreen from "@/components/LoadingScreen";
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

const Index = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { language, t } = useLanguage();

  useEffect(() => {
    // Simuler un temps de chargement pour le débogage
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const partners = [
    {
      id: 1,
      name: "Laboratoire 1",
      logo: "/partners/logo1.png"
    },
    {
      id: 2,
      name: "Laboratoire 2",
      logo: "/partners/logo2.png"
    },
    {
      id: 3,
      name: "Laboratoire 3",
      logo: "/partners/logo3.png"
    },
    {
      id: 4,
      name: "Laboratoire 4",
      logo: "/partners/logo4.png"
    },
    {
      id: 5,
      name: "Laboratoire 5",
      logo: "/partners/logo5.png"
    },
    {
      id: 6,
      name: "Laboratoire 6",
      logo: "/partners/logo6.png"
    }
  ];

  return (
    <>
      <Helmet>
        <title>{language === 'en' ? 'LeaderProMed | Medical Promoter in Mauritania and North Africa' : 'LeaderProMed | Promoteur médical en Mauritanie et Afrique du Nord'}</title>
        <meta name="description" content={language === 'en' ? 'LeaderProMed, your medical partner in Mauritania and North Africa: promotion, delegation, distribution of pharmaceutical products, medical devices, supplements and more. Discover our partner laboratories and our regional expertise.' : 'LeaderProMed, votre partenaire médical en Mauritanie et Afrique du Nord : promotion, délégation, distribution de produits pharmaceutiques, dispositifs médicaux, compléments et plus. Découvrez nos laboratoires partenaires et notre expertise régionale.'} />
        <meta name="keywords" content={language === 'en' ? 'medical, Mauritania, North Africa, pharmaceutical registration, drug registration, regulatory affairs, market access, Africa, international laboratory, pharmaceutical products, medical devices, LeaderProMed, registration, medicine registration, pharma, clinical trials, licensing, distributor, healthcare, B2B, partnership' : 'médical, Mauritanie, Afrique du Nord, enregistrement pharmaceutique, enregistrement médicament, affaires réglementaires, accès au marché, laboratoire international, produits pharmaceutiques, dispositifs médicaux, LeaderProMed, enregistrement, essais cliniques, licence, distributeur, santé, B2B, partenariat'} />
        <meta property="og:title" content={language === 'en' ? 'LeaderProMed | Medical Promoter in Mauritania and North Africa' : 'LeaderProMed | Promoteur médical en Mauritanie et Afrique du Nord'} />
        <meta property="og:description" content={language === 'en' ? 'LeaderProMed, your medical partner in Mauritania and North Africa: promotion, delegation, distribution of pharmaceutical products, medical devices, supplements and more.' : 'LeaderProMed, votre partenaire médical en Mauritanie et Afrique du Nord : promotion, délégation, distribution de produits pharmaceutiques, dispositifs médicaux, compléments et plus.'} />
        <meta property="og:image" content="/public/logo.png" />
        <meta property="og:url" content="https://leaderpromed.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={language === 'en' ? 'LeaderProMed | Medical Promoter in Mauritania and North Africa' : 'LeaderProMed | Promoteur médical en Mauritanie et Afrique du Nord'} />
        <meta name="twitter:description" content={language === 'en' ? 'LeaderProMed, your medical partner in Mauritania and North Africa: promotion, delegation, distribution of pharmaceutical products, medical devices, supplements and more.' : 'LeaderProMed, votre partenaire médical en Mauritanie et Afrique du Nord : promotion, délégation, distribution de produits pharmaceutiques, dispositifs médicaux, compléments et plus.'} />
        <meta name="twitter:image" content="/public/logo.png" />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        {/* Hero Section with Background Image */}
        <section 
          className="relative flex items-center justify-center bg-cover bg-center min-h-[70vh]"
          style={{ backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/background.jpg')" }}
        >
          <div className="text-center px-4 z-10">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl text-white max-w-2xl mx-auto mb-8">
              {t('home.hero.subtitle')}
            </p>
            <Link to="/services">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2">
                {t('home.hero.cta')}
              </Button>
            </Link>
          </div>
        </section>

        {/* Three Cards Section */}
        <section className="container mx-auto px-4 py-16 -mt-20 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 - Existence */}
            <Card className="bg-white shadow-lg transition-transform hover:scale-105">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto bg-blue-100 p-4 rounded-full mb-4">
                  <Activity size={36} className="text-blue-600" />
                </div>
                <CardTitle className="text-xl text-blue-600">Notre Expertise</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-gray-600">
                  Plus de 8 ans d'expérience dans le domaine de la promotion médicale en Mauritanie, avec une équipe de professionnels dévoués.
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-2 justify-center">
                <Button variant="outline" className="text-blue-600 border-blue-600 hover:bg-blue-50">
                  <Link to="/about">{t('home.cards.existence.cta')}</Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Card 2 - Partnership */}
            <Card className="bg-white shadow-lg transition-transform hover:scale-105">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto bg-blue-100 p-4 rounded-full mb-4">
                  <Handshake size={36} className="text-blue-600" />
                </div>
                <CardTitle className="text-xl text-blue-600">Nos Partenaires</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-gray-600">
                  Nous collaborons avec les meilleurs laboratoires internationaux pour apporter des solutions médicales innovantes en Mauritanie.
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-2 justify-center">
                <Button variant="outline" className="text-blue-600 border-blue-600 hover:bg-blue-50">
                  <Link to="/partners">{t('home.cards.partnership.cta')}</Link>
                </Button>
              </CardFooter>
            </Card>

            {/* Card 3 - Promotion */}
            <Card className="bg-white shadow-lg transition-transform hover:scale-105">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto bg-blue-100 p-4 rounded-full mb-4">
                  <Award size={36} className="text-blue-600" />
                </div>
                <CardTitle className="text-xl text-blue-600">Promotion Médicale</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-gray-600">
                  Des stratégies de promotion efficaces pour les produits médicaux, adaptées au marché mauritanien et aux besoins locaux.
                </CardDescription>
              </CardContent>
              <CardFooter className="pt-2 justify-center">
                <Button variant="outline" className="text-blue-600 border-blue-600 hover:bg-blue-50">
                  <Link to="/services">{t('home.cards.promotion.cta')}</Link>
                </Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        {/* Who We Are Section */}
        <section className="bg-blue-50 py-16">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="lg:w-1/2">
                <div className="relative">
                  <div className="bg-white p-4 shadow-lg rounded-lg">
                    <img 
                      src="/background.jpg" 
                      alt="Équipe Leader ProMed - Agence Pharmaceutique en Mauritanie" 
                      className="rounded w-full h-auto object-cover"
                      style={{ maxHeight: "400px" }} 
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-4 rounded-lg shadow-lg hidden md:block">
                    <Building size={40} />
                  </div>
                </div>
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-3xl font-bold text-blue-600 mb-6">Qui Nous Sommes</h2>
                <div className="h-1 w-20 bg-blue-600 mb-6"></div>
                <p className="text-gray-700 mb-6">
                  Leader ProMed (LPM) est une <Link to="/about" className="text-blue-600 hover:underline">{t('home.whoWeAre.description.part1')}</Link> en Mauritanie. Elle agit comme un intermédiaire stratégique entre les <Link to="/partners" className="text-blue-600 hover:underline">{t('home.whoWeAre.description.part2')}</Link> et les grossistes agréés en Mauritanie, tout en facilitant les relations avec le ministère de la Santé.
                </p>
                <p className="text-gray-700 mb-8">
                  Avec une équipe de professionnels qualifiés et une connaissance approfondie du secteur de la santé en Mauritanie, nous offrons des <Link to="/services" className="text-blue-600 hover:underline">{t('home.whoWeAre.description.part3')}</Link> pour répondre aux besoins spécifiques de nos partenaires et contribuer à l'amélioration des soins de santé dans le pays.
                </p>
                <Link to="/about">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    {t('home.whoWeAre.cta')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-blue-600 mb-2">Nos Partenaires</h2>
              <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Nous travaillons avec les laboratoires pharmaceutiques internationaux les plus réputés pour offrir des produits de qualité au marché mauritanien.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {partners.map((partner) => (
                <div key={partner.id} className="flex items-center justify-center p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                  <div className="h-20 w-full flex items-center justify-center">
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        
        {/* News & Events Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-blue-600 mb-2">Actualités & Événements</h2>
              <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Restez informés sur nos dernières actualités et événements dans le secteur médical.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* News Cards */}
              {[
                { 
                  id: 1,
                  title: "Réunion scientifique sur le pied diabétique", 
                  date: "31 Mai 2025",
                  content: "L'Association Mauritanienne des Maladies Endocriniennes, du Diabète et de la Nutrition a organisé une réunion scientifique intitulée 'Le pied diabétique : de la prévention au traitement des complications'. Cette conférence a réuni des experts médicaux pour discuter des meilleures pratiques de prévention et de traitement du pied diabétique en Mauritanie.",
                  image: "/images/news/1.jpg"
                },
                { 
                  id: 2,
                  title: "4e Congrès Maghrébin de Chirurgie Orthopédique", 
                  date: "16 Mai 2025",
                  content: "Nous partageons avec vous quelques moments du premier jour du 4e Congrès Maghrébin - Congrès National de Chirurgie Orthopédique et Traumatologique, organisé par la Société Mauritanienne de la Chirurgie Orthopédique et Traumatologique. Cet événement majeur rassemble les meilleurs spécialistes du Maghreb pour échanger sur les dernières avancées en chirurgie orthopédique.",
                  image: "/images/news/2.jpg"
                },
                { 
                  id: 3,
                  title: "2e Congrès National des Médecins Mauritaniens", 
                  date: "11 Mai 2025",
                  content: "Les moments forts du 2e Congrès National de l'Ordre National des Médecins Mauritaniens, qui s'est tenu hier et se poursuit aujourd'hui, ont été capturés afin de conserver une trace vivante de cette rencontre majeure. Cet événement rassemble l'élite du corps médical mauritanien pour discuter des enjeux actuels de la profession.",
                  image: "/images/news/3.jpg"
                }
              ].map((news) => (
                <Card key={news.id} className="bg-white shadow-lg overflow-hidden">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={news.image || "/background.jpg"} 
                      alt={`${news.title} - Leader ProMed Mauritanie`} 
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                    />
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2 text-blue-600 text-sm">
                      <Calendar size={16} />
                      <span>{news.date}</span>
                    </div>
                    <CardTitle className="text-xl">{news.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {news.content || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua."}
                    </CardDescription>
                  </CardContent>
                  <CardFooter>
                    <Link to={`/news/${news.id}`}>
                      <Button variant="link" className="text-blue-600 p-0 hover:text-blue-800">
                        Lire plus &rarr;
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/news">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Toutes Les Actualités
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-16 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-blue-600 mb-2">Galerie</h2>
              <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Découvrez nos événements, formations et activités en images.
              </p>
            </div>

            <div className="relative px-10">
              <Carousel>
                <CarouselContent>
                  {[
                    {
                      image: "/images/news/orthopedie/1.png",
                      title: "Congrès d'Orthopédie - Session 1",
                      date: "Mars 2024"
                    },
                    {
                      image: "/images/news/orthopedie/2.png",
                      title: "Congrès d'Orthopédie - Session 2",
                      date: "Mars 2024"
                    },
                    {
                      image: "/images/news/orthopedie/4.png",
                      title: "Congrès d'Orthopédie - Session 3",
                      date: "Mars 2024"
                    },
                    {
                      image: "/images/news/somedian/1.png",
                      title: "Congrès SOMEDIAN - Session 1",
                      date: "Mars 2024"
                    },
                    {
                      image: "/images/news/somedian/2.png",
                      title: "Congrès SOMEDIAN - Session 2",
                      date: "Mars 2024"
                    },
                    {
                      image: "/images/news/somedian/3.png",
                      title: "Congrès SOMEDIAN - Session 3",
                      date: "Mars 2024"
                    }
                  ].map((item, index) => (
                    <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                      <div className="p-1">
                        <div className="overflow-hidden rounded-lg shadow-lg bg-white">
                          <div className="relative h-64 overflow-hidden">
                            <img 
                              src={item.image} 
                              alt={`${item.title} - Leader ProMed Mauritanie`}
                              className="w-full h-full object-cover transition-transform hover:scale-105" 
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                              <span className="text-white font-medium">{item.title} - {item.date}</span>
                            </div>
                            <div className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full">
                              <GalleryHorizontal size={20} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="-left-4" />
                <CarouselNext className="-right-4" />
              </Carousel>
            </div>

            <div className="text-center mt-10">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                <Link to="/gallery">{t('home.gallery.cta')}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-blue-600 mb-2">Contactez-Nous</h2>
              <div className="h-1 w-20 bg-blue-600 mb-4 mx-auto"></div>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Nous sommes toujours disponibles pour répondre à vos questions et besoins.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-1/2">
                <div className="bg-white p-8 rounded-lg shadow-lg">
                  <h3 className="text-xl font-bold text-blue-600 mb-4">Envoyez-nous un message</h3>
                  <form className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 mb-2">Nom</label>
                        <input 
                          type="text" 
                          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={t('home.contact.form.name')}
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-2">Email</label>
                        <input 
                          type="email" 
                          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={t('home.contact.form.email')}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2">Sujet</label>
                      <input 
                        type="text" 
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t('home.contact.form.subject')}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2">Message</label>
                      <textarea 
                        className="w-full p-3 border border-gray-300 rounded-md h-32 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t('home.contact.form.message')}
                      ></textarea>
                    </div>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3">
                      {t('home.contact.form.cta')}
                    </Button>
                  </form>
                </div>
              </div>
              <div className="lg:w-1/2">
                <div className="bg-blue-50 p-8 rounded-lg shadow-lg h-full">
                  <h3 className="text-xl font-bold text-blue-600 mb-6">Informations de contact</h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <Contact size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">Adresse</h4>
                        <p className="text-gray-600">{t('home.contact.address')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <Mail size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">Email</h4>
                        <p className="text-gray-600">{t('home.contact.email')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                        <Phone size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">Téléphone</h4>
                        <p className="text-gray-600">{t('home.contact.phone')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <h4 className="font-bold text-gray-800 mb-4">Suivez-nous</h4>
                    <div className="flex gap-4">
                      <a href="https://www.facebook.com/profile.php?id=100086261242323" className="bg-blue-100 p-3 rounded-full text-blue-600 hover:bg-blue-200 transition-colors">
                        <Facebook size={24} />
                      </a>
                      <a href="https://www.linkedin.com/company/leaderpromed" className="bg-blue-100 p-3 rounded-full text-blue-600 hover:bg-blue-200 transition-colors">
                        <Linkedin size={24} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Index;
